import { Logger, LoggerClass } from '_common/services';
import { EditorDOMElements } from 'Editor/services/_Common/DOM';
import { JsonRange } from 'Editor/services/_Common/Selection';
import { NodeUtils } from 'Editor/services/DataManager';
import ReduxInterface from 'Editor/services/ReduxInterface';
import ViewModelUtils from 'Editor/services/VisualizerManager/ViewModels/ViewModelUtils';

export class SelectionStatusHandler {
  private editionContext: Editor.Edition.Context;
  private stylesContext: Editor.Edition.Styles.Context;

  private timeouts: {
    checkStylesStatus?: NodeJS.Timeout;
    updateSelectedElements?: NodeJS.Timeout;
    updateContentStyles?: NodeJS.Timeout;
    updateDocumentStyles?: NodeJS.Timeout;

    selectTask?: NodeJS.Timeout;
    dismissTask?: NodeJS.Timeout;
    selectComment?: NodeJS.Timeout;
    deselectComments?: NodeJS.Timeout;
  } = {};

  DEBUG: boolean = false;

  constructor(
    editionContext: Editor.Edition.Context,
    stylesContext: Editor.Edition.Styles.Context,
  ) {
    this.editionContext = editionContext;
    this.stylesContext = stylesContext;

    this.checkSelectionStatus = this.checkSelectionStatus.bind(this);
  }

  initialize() {}

  destroy() {
    if (this.timeouts.checkStylesStatus) {
      clearTimeout(this.timeouts.checkStylesStatus);
      delete this.timeouts.checkStylesStatus;
    }

    if (this.timeouts.updateSelectedElements) {
      clearTimeout(this.timeouts.updateSelectedElements);
      delete this.timeouts.updateSelectedElements;
    }

    if (this.timeouts.updateContentStyles) {
      clearTimeout(this.timeouts.updateContentStyles);
      delete this.timeouts.updateContentStyles;
    }

    if (this.timeouts.updateDocumentStyles) {
      clearTimeout(this.timeouts.updateDocumentStyles);
      delete this.timeouts.updateDocumentStyles;
    }

    if (this.timeouts.selectTask) {
      clearTimeout(this.timeouts.selectTask);
      delete this.timeouts.selectTask;
    }

    if (this.timeouts.dismissTask) {
      clearTimeout(this.timeouts.dismissTask);
      delete this.timeouts.dismissTask;
    }

    if (this.timeouts.selectComment) {
      clearTimeout(this.timeouts.selectComment);
      delete this.timeouts.selectComment;
    }

    if (this.timeouts.deselectComments) {
      clearTimeout(this.timeouts.deselectComments);
      delete this.timeouts.deselectComments;
    }
  }

  traceMessage(...args: Parameters<LoggerClass['trace']>) {
    if (this.DEBUG) {
      Logger.trace(...args);
    }
  }

  debounceCheckSelectionStatus() {
    if (this.timeouts.checkStylesStatus) {
      clearTimeout(this.timeouts.checkStylesStatus);
    }

    this.timeouts.checkStylesStatus = setTimeout(() => {
      this.checkSelectionStatus();
    }, 50);
  }

  private checkSelectionStatus() {
    try {
      this.traceMessage('[SelectionStatusHandler] checkSelectionStatus');

      const rangeData = this.editionContext.DataManager?.selection?.current?.[0];
      if (!rangeData) {
        return;
      }
      const range: Editor.Selection.JsonRange = JsonRange.buildFromRangeData(rangeData);

      // selected elements
      if (this.timeouts.updateSelectedElements) {
        clearTimeout(this.timeouts.updateSelectedElements);
      }

      this.timeouts.updateSelectedElements = setTimeout(() => {
        this.updateSelectedElementsStatus(range);
      }, 0);

      // inline selection status
      if (this.timeouts.updateContentStyles) {
        clearTimeout(this.timeouts.updateContentStyles);
      }

      this.timeouts.updateContentStyles = setTimeout(() => {
        this.updateContentStylesStatus(range);
      }, 0);

      // document styles status
      if (this.timeouts.updateDocumentStyles) {
        clearTimeout(this.timeouts.updateDocumentStyles);
      }

      this.timeouts.updateDocumentStyles = setTimeout(() => {
        this.updateDocumentStylesStatus(range);
      }, 0);
    } catch (error) {
      Logger.warn(error);
    }
  }

  private checkSingleBlockStatus(
    range: Editor.Selection.JsonRange,
    currentStatus: Editor.Edition.Styles.ContentStatus,
    previousStatus: Editor.Edition.Styles.ContentStatus,
  ) {
    if (!this.editionContext.DataManager || !this.editionContext.VisualizerManager) {
      return;
    }

    if (range.start.b !== range.end.b) {
      return;
    }

    const baseModel = this.editionContext.DataManager.nodes.getNodeModelById(range.start.b);
    const baseData = baseModel?.selectedData();

    if (baseModel && baseData?.id) {
      currentStatus.BLOCK = {
        id: baseData.id,
        type: baseData.type,
        approved: baseModel.isApproved(),
      };

      currentStatus.BLOCK_IDS.push(baseData.id);

      // check if element is editable
      if (!this.editionContext.DataManager.nodes.isNodeEditable(baseData.id)) {
        currentStatus.EDITABLE = false;
      }

      const closestContainer = NodeUtils.closestOfTypeByPath(
        baseData,
        range.getCommonAncestorPath(),
        ['rs', 'toc', 'tol', 'tof', 'tot', 'tbl'],
      );

      if (closestContainer?.data?.id) {
        // check ref section
        if (NodeUtils.isReferenceSectionData(closestContainer.data)) {
          currentStatus.REFSECTION = {
            id: closestContainer.data.id,
          };
        }

        // check table o contents
        if (NodeUtils.isTableOfContentsData(closestContainer.data)) {
          currentStatus.TOC = {
            id: closestContainer.data.id,
          };
        }

        // check table of labels
        if (NodeUtils.isTableOfLabelsData(closestContainer.data)) {
          const data = this.editionContext.DataManager?.tableOfContents.getPropertiesFromTOLSection(
            baseData.id,
            closestContainer.data.id,
          );

          currentStatus.TOL = {
            id: closestContainer.data.id,
            label: data?.label || '',
          };
        }

        // check legacy list of figures
        if (NodeUtils.isLegacyTableOfFiguresData(closestContainer.data)) {
          currentStatus.TOL = {
            id: closestContainer.data.id,
            label: 'Figure',
          };
        }

        // check tables
        // TODO: find a way to avoid getting selected table information from html elements
        if (NodeUtils.isTableData(closestContainer.data)) {
          // TODO: find a better way to get the html view
          const tableView = document.getElementById(closestContainer.data.id);

          if (EditorDOMElements.isTableElement(tableView)) {
            const selectedCells = tableView.getSelectedCellsInfo?.() || [];
            const selectedRows = tableView.getSelectedRows();

            if (selectedCells.length === 0) {
              const closestCell = NodeUtils.closestOfTypeByPath(
                baseData,
                range.getCommonAncestorPath(),
                ['tblc'],
              );

              if (closestCell?.data?.id) {
                // TODO: find a better way to get the html view
                const cellView = document.getElementById(closestCell.data.id);

                if (EditorDOMElements.isTableCellElement(cellView)) {
                  selectedCells.push(cellView.getCellInfo());
                }
              }
            }

            currentStatus.TABLE = {
              id: closestContainer.data.id,
              selectedCells: selectedCells,
              rows: { fromStart: selectedRows[0]?.id === tableView.tBodies[0].rows[0]?.id },
            };
          }
        }
      }

      // check for images
      const closestImage = NodeUtils.closestOfTypeByPath(baseData, range.getCommonAncestorPath(), [
        'img',
        'figure',
      ]);

      if (closestImage?.data.id) {
        let imageId: string;
        if (NodeUtils.isFigureData(closestImage)) {
          imageId = closestImage.childNodes?.[0]?.id || '';
        } else {
          imageId = closestImage.data.id;
        }

        currentStatus.IMAGE = {
          id: imageId,
        };
        currentStatus.TEXT = false;
      }

      // check for tasks
      if (baseData.tasks?.length) {
        currentStatus.TASK = {
          blockId: baseData.id,
          tasks: baseData.tasks,
        };
      }

      // check lists
      if (NodeUtils.isParagraphData(baseData)) {
        const listId = this.editionContext.DataManager.numbering.getListIdFromBlock(baseData.id);
        if (listId) {
          currentStatus.LIST = {
            id: listId,
            style: this.editionContext.DataManager.numbering.getStyleIdForList(listId),
          };
        }
      }

      // check for tabulations
      const viewModel = this.editionContext.VisualizerManager.getViewModelById(baseData.id);
      if (viewModel) {
        let tabs: Editor.Data.TabStop[] = [];
        const view = viewModel.getRootView();
        if (view) {
          tabs = this.editionContext.VisualizerManager.getTabStops(view) || [];
        }
        if (tabs) {
          for (let i = 0; i < tabs.length; i++) {
            const tab: Editor.Data.TabStop = tabs[i];
            if (tab.t !== 'h') {
              currentStatus.TABULATIONS.push({
                leader: tab.l || 'n',
                type: tab.t,
                value: tab.v,
              });
            }
          }
        }
      }

      // check for section properties
      const section = this.editionContext.DataManager.sections.getSectionOfBlock(baseData.id);
      if (section) {
        const attributes = this.editionContext.DataManager.sections.getSectionAttributes(section);
        if (attributes) {
          currentStatus.SECTION = {
            id: section,
            properties: attributes,
            numberOfSections: this.editionContext.DataManager.sections?.numberOfSections || 0,
          };
        }
      }

      // check inline elements
      this.checkInlineElementsStatus(range, baseModel, currentStatus, previousStatus);
    }
  }

  private checkMultipleBlockStatus(
    range: Editor.Selection.JsonRange,
    currentStatus: Editor.Edition.Styles.ContentStatus,
    previousStatus: Editor.Edition.Styles.ContentStatus,
  ) {
    if (!this.editionContext.DataManager || !this.editionContext.VisualizerManager) {
      return;
    }

    const blockRangesData = JsonRange.splitRangeByTypes(
      this.editionContext.DataManager,
      range,
      NodeUtils.BLOCK_TYPES,
      {
        sameContainerLevel: false,
        useSelectedCells: true,
      },
    );

    for (let i = 0; i < blockRangesData.length; i++) {
      const subRange = blockRangesData[i].range;

      const baseModel = this.editionContext.DataManager.nodes.getNodeModelById(subRange.start.b);

      if (!baseModel) {
        continue;
      }

      if (!currentStatus.BLOCK_IDS.includes(baseModel.id)) {
        currentStatus.BLOCK_IDS.push(baseModel.id);
      }

      // check if element is editable
      if (!this.editionContext.DataManager.nodes.isNodeEditable(baseModel.id)) {
        currentStatus.EDITABLE = false;
      }

      // check inline elements
      this.checkInlineElementsStatus(subRange, baseModel, currentStatus, previousStatus);
    }
  }

  private checkInlineElementsStatus(
    range: Editor.Selection.JsonRange,
    baseModel: Editor.Data.Node.Model,
    currentStatus: Editor.Edition.Styles.ContentStatus,
    previousStatus: Editor.Edition.Styles.ContentStatus,
  ) {
    if (!this.editionContext.DataManager) {
      return;
    }

    const suggestionRefsFound: string[] = [];
    const commentRefsFound: string[] = [];

    const typesToCheck: (keyof Editor.Data.Node.DataTypeMapper)[] = [
      'equation',
      'note',
      'citations-group',
      'comment',
      'temp-comment',
      'tracked-insert',
      'tracked-delete',
      'f',
      'link',
    ];

    const baseData = baseModel?.selectedData();
    const nodes = range.getNodes(this.editionContext.DataManager, typesToCheck);

    if (baseData) {
      let currentPath = range.getCommonAncestorPath();
      let closestNode = NodeUtils.closestOfTypeByPath(baseData, currentPath, typesToCheck);

      while (closestNode) {
        nodes.push({
          baseData,
          childData: closestNode.data,
          childPath: closestNode.path,
        });

        if (!currentPath.length) break;

        currentPath = currentPath.slice(0, -2);

        closestNode = NodeUtils.closestOfTypeByPath(baseData, currentPath, typesToCheck);
      }
    }

    for (let i = 0; i < nodes.length; i++) {
      const baseData = nodes[i].baseData;
      const data = nodes[i].childData;
      const path = nodes[i].childPath;

      if (!data || !data.id) {
        continue;
      }

      // collapsed range cases
      if (range.isCollapsed()) {
        // fields
        if (NodeUtils.isFieldData(data)) {
          // is field data
          const closestContainer = NodeUtils.closestOfTypeByPath(baseData, path, ['rs']);
          // check if it is inside a Reference Section
          if (!closestContainer) {
            currentStatus.FIELD = {
              type: data.properties.t,
              id: data.id,
            };
            if (data.properties.t === 'cpt') {
              currentStatus.FIELD.label = data.properties.cpt;
            }
          }
        }

        // hyperlink data
        if (NodeUtils.isHyperlinkData(data)) {
          currentStatus.LINK = {
            id: data.id,
          };
        }
      }

      // equations
      if (NodeUtils.isEquationData(data)) {
        if (!baseModel?.isApproved()) {
          currentStatus.EQUATION = {
            id: data.id,
          };
        }
      }

      // notes
      if (NodeUtils.isNoteData(data)) {
        currentStatus.NOTE = { id: data.id, type: data.properties.note_type };
      }

      // citations
      if (NodeUtils.isCitationsGroupData(data)) {
        currentStatus.CITATION = { id: data.id };
      }

      // comments and temp-comments
      if (NodeUtils.isCommentData(data)) {
        const ref = data.properties.element_reference;

        if (ref && !commentRefsFound.includes(ref)) {
          commentRefsFound.push(ref);

          currentStatus.COMMENT.push({
            ref,
          });

          const author = data.properties.author || data.properties.user || '';
          if (
            data.type === 'temp-comment' /* && !element.hidden */ &&
            this.editionContext.DataManager.users.isLoggedUser(author)
          ) {
            ReduxInterface.openTemporaryCommentCard({
              reference: ref,
              level0: baseModel.id,
            });
          }
        }
      }

      // tracked insert and tracked delete elements
      if (NodeUtils.isTrackedData(data) /* && !element.hidden */) {
        const ref = data.properties.element_reference;

        if (ref && !suggestionRefsFound.includes(ref)) {
          suggestionRefsFound.push(ref);

          currentStatus.TRACKED.push({
            ref,
          });
        }
      }
    }
  }

  private selectCurrentElements(
    currentStatus: Editor.Edition.Styles.ContentStatus,
    previousStatus: Editor.Edition.Styles.ContentStatus,
  ) {
    if (!this.editionContext.VisualizerManager) {
      return;
    }

    const renderMode = this.editionContext.VisualizerManager.getRenderMode();
    const widgetsManager = this.editionContext.VisualizerManager.getWidgetsManager();

    // TODO: find a better way to get html views, avoiding document.getElementById

    // select table and add table options widget
    if (currentStatus.TABLE) {
      const element = document.getElementById(currentStatus.TABLE.id);

      if (
        EditorDOMElements.isTableElement(element) &&
        !widgetsManager?.isAnyWidgetOpenForView(element.id)
      ) {
        widgetsManager?.addWidget('tableOptions', element, {});
      }
    }

    // select reference section
    if (currentStatus.REFSECTION) {
      const element = document.getElementById(currentStatus.REFSECTION.id);

      if (EditorDOMElements.isReferencesSectionElement(element)) {
        element.select();
      }
    }

    // select images
    if (currentStatus.IMAGE && currentStatus.BLOCK && !currentStatus.BLOCK.approved) {
      const imageElement = document.getElementById(currentStatus.IMAGE.id);
      if (EditorDOMElements.isImageElement(imageElement)) {
        widgetsManager?.addWidget('resizable', imageElement, {});
        widgetsManager?.addWidget('imageOptions', imageElement, {});
      }
    }

    // select tasks
    if (currentStatus.TASK) {
      if (
        JSON.stringify(previousStatus?.TASK) !== JSON.stringify(currentStatus.TASK) &&
        renderMode === 'BASIC'
      ) {
        if (this.timeouts.selectTask) {
          clearTimeout(this.timeouts.selectTask);
        }

        const blockId = currentStatus.TASK.blockId;

        this.timeouts.selectTask = setTimeout(() => {
          const viewModel = this.editionContext.VisualizerManager?.getViewModelById(blockId);

          if (ViewModelUtils.isBlockViewModel(viewModel)) {
            viewModel.setTaskSelected(true);
          }
        }, 0);
      }
    }

    // select field element
    if (currentStatus.FIELD) {
      const element = document.getElementById(currentStatus.FIELD.id);

      if (EditorDOMElements.isFieldElement(element)) {
        element.selectField();
      }
    }

    // select hyperlink element
    if (currentStatus.LINK) {
      const element = document.getElementById(currentStatus.LINK.id);

      if (EditorDOMElements.isHyperlinkElement(element)) {
        element.select();
      }
    }

    // select equantion element
    if (currentStatus.EQUATION) {
      const element = document.getElementById(currentStatus.EQUATION.id);

      if (EditorDOMElements.isEquationElement(element)) {
        element.selected = true;

        widgetsManager?.addWidget('equationOptions', element, {});
      }
    }

    // select comments and track changes
    const refsData = [...currentStatus.COMMENT, ...currentStatus.TRACKED];
    if (refsData.length && renderMode === 'BASIC') {
      if (this.timeouts.selectComment) {
        clearTimeout(this.timeouts.selectComment);
      }

      const query = refsData.reduce((query, data) => {
        if (query === '') {
          query += `[element_reference="${data.ref}"]`;
        } else {
          query += `,[element_reference="${data.ref}"]`;
        }
        return query;
      }, '');

      this.timeouts.selectComment = setTimeout(() => {
        const elements = document.querySelectorAll(query);

        for (let i = 0; i < elements.length; i++) {
          const element = elements.item(i);
          if (
            EditorDOMElements.isCommentElement(element) ||
            EditorDOMElements.isTemporaryCommentelement(element) ||
            EditorDOMElements.isTrackDeleteElement(element) ||
            EditorDOMElements.isTrackInsertElement(element)
          ) {
            element.selectElement();
          }
        }
      }, 0);
    }
  }

  private deselectPreviousElements(
    currentStatus: Editor.Edition.Styles.ContentStatus,
    previousStatus?: Editor.Edition.Styles.ContentStatus,
  ) {
    if (!this.editionContext.VisualizerManager || !previousStatus) {
      return;
    }

    const widgetsManager = this.editionContext.VisualizerManager.getWidgetsManager();

    // TODO: find a better way to get html views, avoiding document.getElementById

    // deselect tasks
    if (previousStatus.TASK) {
      if (JSON.stringify(previousStatus.TASK) !== JSON.stringify(currentStatus.TASK)) {
        if (this.timeouts.dismissTask) {
          clearTimeout(this.timeouts.dismissTask);
        }

        const blockId = previousStatus.TASK.blockId;

        this.timeouts.dismissTask = setTimeout(() => {
          const viewModel = this.editionContext.VisualizerManager?.getViewModelById(blockId);

          if (ViewModelUtils.isBlockViewModel(viewModel)) {
            viewModel.setTaskSelected(false);
          }
        }, 0);
      }
    }

    // close image widgets
    if (previousStatus.IMAGE?.id && currentStatus.IMAGE?.id !== previousStatus.IMAGE?.id) {
      const prevImage = document.getElementById(previousStatus.IMAGE?.id);
      if (
        prevImage &&
        (EditorDOMElements.isFigureElement(prevImage) ||
          EditorDOMElements.isImageElement(prevImage))
      ) {
        widgetsManager?.removeWidget('resizable', prevImage.id);
        widgetsManager?.removeWidget('imageOptions', prevImage.id);
      }
    }

    // deselect equation and close widgets
    if (previousStatus.EQUATION?.id && currentStatus.EQUATION?.id !== previousStatus.EQUATION?.id) {
      const prevEquation = document.getElementById(previousStatus.EQUATION?.id);

      if (EditorDOMElements.isEquationElement(prevEquation)) {
        prevEquation.selected = false;
        widgetsManager?.removeWidget('equationOptions', prevEquation.id);
      }
    }

    // deselect table cells and close table widgets
    if (previousStatus.TABLE?.id && currentStatus.TABLE?.id !== previousStatus.TABLE?.id) {
      const prevTable = document.getElementById(previousStatus.TABLE?.id);
      if (EditorDOMElements.isTableElement(prevTable)) {
        prevTable.deselectAllCells();
      }

      widgetsManager?.removeWidget('tableOptions', previousStatus.TABLE?.id);
      widgetsManager?.removeWidget('resizable', previousStatus.TABLE?.id);
    }

    // deleselect ref section
    if (
      previousStatus.REFSECTION != null &&
      previousStatus.REFSECTION !== false &&
      (!currentStatus.REFSECTION || previousStatus.REFSECTION.id !== currentStatus.REFSECTION.id)
    ) {
      const selectedRefSection = document.getElementById(previousStatus.REFSECTION.id);
      if (EditorDOMElements.isReferencesSectionElement(selectedRefSection)) {
        selectedRefSection.deselect();
      }
    }

    // deleselect fields elements
    if (previousStatus.FIELD != null && previousStatus.FIELD.id !== currentStatus.FIELD?.id) {
      const selectedField = document.getElementById(previousStatus.FIELD.id);
      if (EditorDOMElements.isFieldElement(selectedField)) {
        selectedField.deselectField();
      }
    }

    // deleselect hyperlink elements
    if (
      previousStatus.LINK != null &&
      previousStatus.LINK !== false &&
      typeof currentStatus.LINK !== 'boolean' &&
      previousStatus.LINK.id !== currentStatus.LINK.id
    ) {
      const link = document.getElementById(previousStatus.LINK.id);
      if (EditorDOMElements.isHyperlinkElement(link)) {
        link.deselect();
      }
    }

    // deselect comments and tracked elements
    const refsToDeselect:
      | Editor.Edition.Styles.ContentStatus['COMMENT']
      | Editor.Edition.Styles.ContentStatus['TRACKED'] = [];

    if (previousStatus.COMMENT.length > 0) {
      const commentRefs = currentStatus.COMMENT.reduce((data: string[], current) => {
        data.push(current.ref);
        return data;
      }, []);

      refsToDeselect.push(
        ...previousStatus.COMMENT.filter((comment) => !commentRefs.includes(comment.ref)),
      );
    }

    if (previousStatus.TRACKED.length !== 0) {
      const trackedRefs = currentStatus.TRACKED.reduce((data: string[], current) => {
        data.push(current.ref);
        return data;
      }, []);

      refsToDeselect.push(
        ...previousStatus.TRACKED.filter((tracked) => !trackedRefs.includes(tracked.ref)),
      );
    }

    if (refsToDeselect.length) {
      if (this.timeouts.deselectComments) {
        clearTimeout(this.timeouts.deselectComments);
      }

      const query = refsToDeselect.reduce((query, data) => {
        if (query === '') {
          query += `[element_reference="${data.ref}"]`;
        } else {
          query += `,[element_reference="${data.ref}"]`;
        }
        return query;
      }, '');

      this.timeouts.deselectComments = setTimeout(() => {
        const elements = document.querySelectorAll(query);

        for (let i = 0; i < elements.length; i++) {
          const element = elements.item(i);
          if (
            EditorDOMElements.isCommentElement(element) ||
            EditorDOMElements.isTemporaryCommentelement(element) ||
            EditorDOMElements.isTrackDeleteElement(element) ||
            EditorDOMElements.isTrackInsertElement(element)
          ) {
            element.deselectElement();
          }
        }
      }, 0);
    }
  }

  private updateSelectedElementsStatus(range: Editor.Selection.JsonRange) {
    if (!this.editionContext.DataManager) {
      return;
    }

    const status: Editor.Edition.Styles.ContentStatus = {
      TEXT: true,
      IMAGE: null,
      TABLE: null,
      EQUATION: null,
      EDITABLE: true,
      COMMENT: [],
      TRACKED: [],
      TASK: false,
      FIELD: null,
      NOTE: false,
      LINK: false,
      REFSECTION: false,
      TOC: null,
      TOL: null,
      BLOCK_IDS: [],
      LANGUAGES: {},
      LIST: false,
      CITATION: false,
      TABULATIONS: [],
      COLLAPSED: true,
      SECTION: null,
      BLOCK: null,
    };

    const previousStatus: Editor.Edition.Styles.ContentStatus =
      ReduxInterface.getEditorState().status?.selection;

    status.COLLAPSED = range.collapsed;

    if (range.start.b === range.end.b) {
      // -----------------------------------------------------
      // check single block
      this.checkSingleBlockStatus(range, status, previousStatus);
    } else {
      // -----------------------------------------------------
      // check multiple block
      this.checkMultipleBlockStatus(range, status, previousStatus);
    }

    if (status.BLOCK_IDS.length) {
      status.LANGUAGES = this.editionContext.DataManager.nodes.getLanguageForNodes(
        status.BLOCK_IDS,
      );
    }

    // -----------------------------------------------------
    // select current elements
    this.selectCurrentElements(status, previousStatus);

    // -----------------------------------------------------
    // deselect previous elements
    this.deselectPreviousElements(status, previousStatus);

    this.traceMessage('[SelectionStatusHandler] updateSelectedElementsStatus', {
      status,
      previousStatus,
    });

    // -----------------------------------------------------
    // update status
    ReduxInterface.setSelectionStatus(status);
  }

  private updateContentStylesStatus(range: Editor.Selection.JsonRange) {
    if (!this.editionContext.DataManager || !this.stylesContext.stylesUtils) {
      return;
    }

    // TODO skip next style checks

    const stylesStatus = this.stylesContext.stylesUtils.getStylesAppliedToSelection(range, {
      includeInheritedStyles: true,
    });

    const styles: Editor.Redux.ToolbarSliceState = {
      fontFamily:
        stylesStatus.fontFamily != null && stylesStatus.fontFamily.length === 1
          ? stylesStatus.fontFamily[0].toLowerCase().replace(/"/g, '')
          : undefined,
      fontSize:
        stylesStatus.fontSize != null && stylesStatus.fontSize.length === 1
          ? stylesStatus.fontSize[0]
          : undefined,
      bold:
        stylesStatus.bold != null && stylesStatus.bold.length === 1 ? stylesStatus.bold[0] : false,
      italic:
        stylesStatus.italic != null && stylesStatus.italic.length === 1
          ? stylesStatus.italic[0]
          : false,
      underline:
        stylesStatus.underline != null && stylesStatus.underline.length === 1
          ? stylesStatus.underline[0]
          : false,
      strikethrough:
        stylesStatus.strikethrough != null && stylesStatus.strikethrough.length === 1
          ? stylesStatus.strikethrough[0]
          : false,
      superscript:
        stylesStatus.superscript != null && stylesStatus.superscript.length === 1
          ? stylesStatus.superscript[0]
          : false,
      subscript:
        stylesStatus.subscript != null && stylesStatus.subscript.length === 1
          ? stylesStatus.subscript[0]
          : false,
      vanish:
        stylesStatus.vanish != null && stylesStatus.vanish.length === 1
          ? stylesStatus.vanish[0]
          : false,
      selectedList:
        stylesStatus.listId != null &&
        stylesStatus.listId.length === 1 &&
        stylesStatus.listId[0] != null
          ? this.editionContext.DataManager.numbering.getStyleIdForList(stylesStatus.listId[0]) ||
            ''
          : '',
      lineHeight:
        stylesStatus.lineHeight != null && stylesStatus.lineHeight.length === 1
          ? stylesStatus.lineHeight[0]
          : 1,
      lineRule:
        stylesStatus.lineRule != null && stylesStatus.lineRule.length === 1
          ? stylesStatus.lineRule[0]
          : undefined,
      spaceBefore:
        stylesStatus.spaceBefore != null && stylesStatus.spaceBefore.length === 1
          ? stylesStatus.spaceBefore[0]
          : 0,
      spaceAfter:
        stylesStatus.spaceAfter != null && stylesStatus.spaceAfter.length === 1
          ? stylesStatus.spaceAfter[0]
          : 0,
      autoSpaceBefore:
        stylesStatus.asb != null && stylesStatus.asb.length === 1 ? stylesStatus.asb[0] : false,
      autoSpaceAfter:
        stylesStatus.asa != null && stylesStatus.asa.length === 1 ? stylesStatus.asa[0] : false,
      widowControl:
        stylesStatus.wc != null && stylesStatus.wc.length === 1 ? stylesStatus.wc[0] : false,
      keepNext:
        stylesStatus.kn != null && stylesStatus.kn.length === 1 ? stylesStatus.kn[0] : false,
      keepLines:
        stylesStatus.kl != null && stylesStatus.kl.length === 1 ? stylesStatus.kl[0] : false,
      contextualSpacing:
        stylesStatus.cts != null && stylesStatus.cts.length === 1 ? stylesStatus.cts[0] : false,
      pageBreakBefore:
        stylesStatus.pbb != null && stylesStatus.pbb.length === 1 ? stylesStatus.pbb[0] : false,
      alignment:
        stylesStatus.alignment != null && stylesStatus.alignment.length === 1
          ? stylesStatus.alignment[0]
          : 'l',
      indentation: {
        leftIndentation:
          stylesStatus.leftIndentation != null && stylesStatus.leftIndentation.length === 1
            ? stylesStatus.leftIndentation[0]
            : 0,
        rightIndentation:
          stylesStatus.rightIndentation != null && stylesStatus.rightIndentation.length === 1
            ? stylesStatus.rightIndentation[0]
            : 0,
        specialIndent:
          stylesStatus.specialIndent != null && stylesStatus.specialIndent.length === 1
            ? stylesStatus.specialIndent[0]
            : 'none',
        specialIndentValue:
          stylesStatus.specialIndentValue != null && stylesStatus.specialIndentValue.length === 1
            ? stylesStatus.specialIndentValue[0]
            : 0,
      },
    };

    this.traceMessage('[SelectionStatusHandler] updateContentStylesStatus', styles);

    ReduxInterface.updateToolbarValues(styles);
  }

  private updateDocumentStylesStatus(range: Editor.Selection.JsonRange) {
    if (!this.stylesContext.documentStyles) {
      return;
    }

    const docStyleStatus = this.stylesContext.documentStyles.getDocumentStyleStatusFromRange(range);

    this.traceMessage('[SelectionStatusHandler] updateDocumentStylesStatus', docStyleStatus);
    ReduxInterface.setSelectedDocumentStyle(docStyleStatus);
  }
}
